<template>
  <div class="stage">
    <div class="blobs" aria-hidden="true">
      <span class="blob b1"></span>
      <span class="blob b2"></span>
      <span class="blob b3"></span>
      <span class="blob b4"></span>
    </div>

    <div class="stage-inner">
      <p class="kicker">Rainbow Manager</p>
      <h1>{{ t('accountDeleteTitle') }}</h1>
      <p class="lead">{{ t('accountDeleteIntro') }}</p>

      <article v-if="deleted" class="card">
        <h2>{{ t('accountDeleteDoneTitle') }}</h2>
        <p>{{ t('accountDeleteDoneText') }}</p>
      </article>

      <template v-else>
        <article class="card">
          <h2>{{ t('accountDeleteWhatTitle') }}</h2>
          <ul>
            <li>{{ t('accountDeleteWhatProfile') }}</li>
            <li>{{ t('accountDeleteWhatGallery') }}</li>
            <li>{{ t('accountDeleteWhatChats') }}</li>
            <li>{{ t('accountDeleteWhatUsage') }}</li>
          </ul>
        </article>

        <article class="card">
          <h2>{{ t('accountDeleteKeepTitle') }}</h2>
          <p>{{ t('accountDeleteKeepStore') }}</p>
        </article>

        <article class="card action">
          <template v-if="email">
            <p>{{ t('accountDeleteSignedInAs', { email }) }}</p>
            <p v-if="confirming" class="danger">{{ t('accountDeleteConfirmQuestion') }}</p>
            <div class="buttons">
              <button
                v-if="confirming"
                class="button ghost"
                type="button"
                :disabled="deleting"
                @click="confirming = false"
              >
                {{ t('accountDeleteCancel') }}
              </button>
              <button class="button remove" type="button" :disabled="deleting" @click="onDelete">
                {{ confirming ? t('accountDeleteConfirmAction') : t('accountDeleteAction') }}
              </button>
            </div>
          </template>
          <template v-else>
            <p>{{ t('accountDeleteSignInFirst') }}</p>
            <div class="buttons">
              <button class="button" type="button" @click="goToSignIn">
                {{ t('accountDeleteSignIn') }}
              </button>
            </div>
          </template>
        </article>

        <article class="card">
          <h2>{{ t('accountDeleteNoAccessTitle') }}</h2>
          <p>{{ t('accountDeleteNoAccessText', { email: SUPPORT_EMAIL }) }}</p>
          <a class="link" :href="`mailto:${SUPPORT_EMAIL}`">{{ SUPPORT_EMAIL }}</a>
        </article>
      </template>
    </div>
  </div>
</template>
<script lang="ts">
import { computed, defineComponent, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/firebase';

const SUPPORT_EMAIL = 'support@rainbow-manager.com';

export default defineComponent({
  setup() {
    const { t } = useI18n();
    const router = useRouter();
    const route = useRoute();
    const store = useStore();
    const confirming = ref(false);
    const deleting = ref(false);
    const deleted = ref(false);
    // main.ts keeps this getter in sync with the restored Firebase session.
    const email = computed(() => store.getters['authFirebase/currentUser']?.email || '');

    // Sign-in sends the user straight back here instead of dropping them into the app.
    const goToSignIn = () => router.push({ name: 'signIn', query: { redirect: route.path } });

    const onDelete = async () => {
      if (!confirming.value) {
        confirming.value = true;
        return;
      }
      deleting.value = true;
      try {
        await httpsCallable(functions, 'deleteAccount')({});
        await store.dispatch('authFirebase/logout');
        deleted.value = true;
      } catch (error) {
        console.error('Account deletion failed', error);
        store.dispatch('toast/addToast', { message: t('accountDeleteError'), severity: 'error' });
      } finally {
        deleting.value = false;
        confirming.value = false;
      }
    };

    return { t, SUPPORT_EMAIL, email, confirming, deleting, deleted, goToSignIn, onDelete };
  },
});
</script>
<style scoped lang="scss">
.stage {
  position: relative;
  isolation: isolate;
  min-height: calc(100dvh - 56px);
  display: flex;
  justify-content: center;
  padding: 32px 28px 64px;
  color: #f6f3ee;
  background: #141018;
}

.blobs {
  position: absolute;
  inset: 0;
  z-index: 0;
  overflow: hidden;
  pointer-events: none;
}

.blob {
  position: absolute;
  border-radius: 50%;
  filter: blur(70px);
  opacity: 0.55;
  animation: blob-drift 22s ease-in-out infinite;
}

.b1 {
  width: 48vw;
  height: 48vw;
  left: -8%;
  top: -18%;
  background: #fa496e;
  animation-duration: 18s;
}

.b2 {
  width: 42vw;
  height: 42vw;
  right: -10%;
  top: 8%;
  background: #a67eff;
  animation-duration: 24s;
  animation-direction: reverse;
}

.b3 {
  width: 38vw;
  height: 38vw;
  left: 18%;
  bottom: -16%;
  background: #52dbff;
  animation-duration: 20s;
}

.b4 {
  width: 28vw;
  height: 28vw;
  right: 18%;
  bottom: 8%;
  background: #a67eff;
  opacity: 0.7;
  animation-duration: 16s;
  animation-direction: reverse;
}

.stage-inner {
  position: relative;
  z-index: 1;
  width: min(640px, 100%);
  text-align: center;
}

.kicker {
  margin: 0 0 16px;
  font-size: 11px;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: rgba(246, 243, 238, 0.7);
}

h1 {
  margin: 0 auto 12px;
  font-size: clamp(2rem, 5vw, 3.2rem);
  font-weight: 600;
  line-height: 1.05;
  letter-spacing: -0.04em;
}

.lead {
  margin: 0 auto 28px;
  max-width: 46ch;
  font-size: 1.05rem;
  line-height: 1.55;
  color: rgba(246, 243, 238, 0.78);
}

.card {
  margin: 0 0 16px;
  padding: 24px;
  border: 1px solid rgba(246, 243, 238, 0.16);
  border-radius: 20px;
  background: rgba(20, 16, 24, 0.28);
  text-align: left;
}

.card h2 {
  margin: 0 0 10px;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: -0.02em;
}

.card p,
.card li {
  margin: 0 0 10px;
  font-size: 15px;
  line-height: 1.55;
  color: rgba(246, 243, 238, 0.78);
}

.card p:last-child,
.card ul:last-child {
  margin-bottom: 0;
}

.card ul {
  margin: 0;
  padding-left: 1.15rem;
}

.danger {
  color: #fa496e;
  font-weight: 600;
}

.action p {
  margin-bottom: 12px;
}

.buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.button {
  min-height: 46px;
  padding: 0 22px;
  border: 0;
  border-radius: 999px;
  background: #f6f3ee;
  color: #141018;
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.04em;
  cursor: pointer;
}

.button:hover {
  background: #fff;
}

.remove {
  color: #f6f3ee;
  background: #fa496e;
}

.remove:hover {
  background: #ff6b88;
}

.ghost {
  color: #f6f3ee;
  background: transparent;
  border: 1px solid rgba(246, 243, 238, 0.28);
}

.ghost:hover {
  background: rgba(246, 243, 238, 0.08);
}

.button:disabled {
  opacity: 0.6;
  cursor: default;
}

.link {
  color: #f6f3ee;
  font-size: 15px;
  text-decoration: none;
}

.link:hover {
  color: #fff;
}

@keyframes blob-drift {
  0%,
  100% {
    transform: translate(0, 0) scale(1);
  }

  50% {
    transform: translate(5%, 7%) scale(1.08);
  }
}

@media (max-width: 600px) {
  .stage {
    padding: 20px 18px 40px;
  }

  .card {
    padding: 20px 16px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .blob {
    animation: none;
  }
}
</style>
